import { ListMagnifyingGlassIcon } from '@phosphor-icons/react/dist/ssr/ListMagnifyingGlass'
import { DynamicIcon } from '@/components/atoms/DynamicIcon'

export type NavItem = {
  key: string
  navBarTitleKey?: string
  description?: string
  path: string
  icon?: React.ReactNode
  display: 'navbar' | 'tab' | 'none'
  children?: NavItem[]
  parent?: NavItem
  backButton?: string
  id?: string
  featureFlagKey?: string
  requiresRotor?: boolean
  authorizedRoles?: string[]
  group?: string
  childGroup?: string
}

export const navItems: NavItem[] = [
  {
    key: 'dashboard',
    path: '/',
    icon: <DynamicIcon name="HouseSimple" className="size-5 shrink-0" />,
    display: 'navbar',
    group: 'main',
  },
  {
    key: 'inventory',
    path: '/inventory',
    icon: <DynamicIcon name="Table" className="size-5 shrink-0" />,
    display: 'navbar',
    group: 'data',
    children: [
      {
        key: 'resources-general',
        path: '/inventory/organization',
        display: 'navbar',
      },
      {
        key: 'resources',
        path: '/inventory',
        display: 'navbar',
      },
      {
        key: 'removal',
        path: '/inventory/removal',
        display: 'navbar',
        backButton: '/inventory',
      },
      {
        key: 'import',
        path: '/inventory/import',
        display: 'navbar',
        backButton: '/inventory',
      },
      {
        key: 'restore',
        path: '/inventory/restore',
        display: 'navbar',
        backButton: '/inventory',
      },
      {
        key: 'references-references',
        path: '/references',
        display: 'navbar',
        childGroup: 'references',
        backButton: '/inventory',
      },
      {
        key: 'references-options',
        path: '/references/options',
        display: 'navbar',
        backButton: '/inventory',
      },
      {
        key: 'references-options',
        path: '/references/options/[id]',
        display: 'none',
        backButton: '/references/options',
      },
      {
        key: 'references-properties',
        path: '/references/properties',
        display: 'navbar',
        backButton: '/inventory',
      },
    ],
  },
  {
    key: 'references',
    path: '/references',
    icon: <DynamicIcon name="ListBullets" className="size-5 shrink-0" />,
    display: 'none',
  },
  {
    key: 'projects',
    navBarTitleKey: 'projects-details',
    path: '/projects',
    icon: <DynamicIcon name="BuildingOffice" className="size-5 shrink-0" />,
    display: 'navbar',
    group: 'projects',
    children: [
      {
        key: 'active-projects',
        path: '/projects',
        display: 'navbar',
      },
      {
        key: 'new-project',
        navBarTitleKey: 'new-project-details',
        path: '/projects/details/create',
        display: 'none',
        backButton: '/projects',
      },
      {
        key: 'project-details',
        navBarTitleKey: 'project-details-details',
        path: '/projects/details/[id]',
        display: 'tab',
        backButton: '/projects',
      },
      {
        key: 'project-team',
        navBarTitleKey: 'project-team-details',
        path: '/projects/team/[id]',
        display: 'tab',
        backButton: '/projects',
      },
      {
        key: 'project-resources',
        path: '/projects/ressources/[id]',
        display: 'tab',
        backButton: '/projects',
      },
      {
        key: 'project-removal',
        path: '/projects/removal/[id]',
        display: 'tab',
        backButton: '/projects',
      },
      {
        key: 'project-sourcing',
        navBarTitleKey: 'project-sourcing-details',
        path: '/projects/sourcing/[id]',
        display: 'tab',
        backButton: '/projects',
      },
      {
        key: 'project-sourcing-results',
        path: '/projects/sourcing-results/[id]',
        display: 'none',
        backButton: '/projects/sourcing/[id]',
      },
      {
        key: 'project-location',
        path: '/projects/location/[id]',
        display: 'tab',
        backButton: '/projects',
        requiresRotor: true,
      },
      {
        key: 'project-catalog',
        navBarTitleKey: 'project-catalog-details',
        path: '/projects/catalog/[id]',
        display: 'tab',
        backButton: '/projects/details/[id]',
        requiresRotor: true,
      },
      {
        key: 'project-statistics',
        navBarTitleKey: 'project-statistics-details',
        path: '/projects/statistics/[id]',
        display: 'tab',
        backButton: '/projects',
      },
      {
        key: 'places',
        path: '/projects/places',
        display: 'navbar',
      },
      {
        key: 'archived-projects',
        navBarTitleKey: 'archived-projects-details',
        path: '/projects/archives',
        display: 'navbar',
      },
    ],
  },
  {
    key: 'alerts',
    path: '/alerts',
    icon: <ListMagnifyingGlassIcon className="size-5 shrink-0" />,
    display: 'navbar',
    group: 'projects',
  },
  {
    key: 'shop',
    navBarTitleKey: 'shop-overview',
    path: '/shop',
    icon: <DynamicIcon name="Storefront" className="size-5 shrink-0" />,
    display: 'navbar',
    group: 'commerce',
    children: [
      {
        key: 'shop-overview',
        path: '/shop',
        display: 'navbar',
      },
      {
        key: 'shop-products',
        path: '/shop/products',
        display: 'navbar',
      },
      {
        key: 'shop-settings',
        path: '/shop/settings/profile',
        display: 'navbar',
        children: [
          {
            key: 'shop-settings-profile',
            path: '/shop/settings/profile',
            display: 'tab',
          },
          {
            key: 'shop-settings-visibility',
            path: '/shop/settings/publication',
            display: 'tab',
          },
          {
            key: 'shop-settings-promotion',
            path: '/shop/settings/seo',
            display: 'tab',
          },
          {
            key: 'shop-settings-reservations',
            path: '/shop/settings/payment',
            display: 'tab',
          },
          {
            key: 'shop-settings-connector',
            path: '/shop/settings/connector',
            display: 'tab',
          },
        ],
      },
    ],
  },
  {
    key: 'orders',
    path: '/orders',
    icon: <DynamicIcon name="Receipt" className="size-5 shrink-0" />,
    display: 'navbar',
    group: 'commerce',
    authorizedRoles: ['ADMIN', 'USER'],
    children: [
      {
        key: 'orders-seller',
        navBarTitleKey: 'orders-seller-details',
        path: '/orders/seller',
        display: 'navbar',
        children: [
          {
            key: 'orders-request',
            path: '/orders/request',
            children: [
              {
                key: 'order-request-details',
                path: '/orders/request/details/[id]',
                display: 'none',
                backButton: '/orders/request',
              },
              {
                key: 'order-seller-quote-details',
                path: '/orders/sentquote/details/[id]',
                display: 'none',
                backButton: '/orders/sentquote',
              },
              {
                key: 'order-seller-details',
                path: '/orders/seller/details/[id]',
                display: 'none',
                backButton: '/orders/seller',
              },
            ],
            display: 'tab',
          },
          {
            key: 'orders-seller-quote',
            navBarTitleKey: 'orders-seller-quote-details',
            path: '/orders/sentquote',
            display: 'tab',
          },
          {
            key: 'orders-seller',
            navBarTitleKey: 'orders-seller-details',
            path: '/orders/seller',
            display: 'tab',
          },
        ],
      },
      {
        key: 'orders-buyer',
        navBarTitleKey: 'order-buyer-details',
        path: '/orders/buyer',
        display: 'navbar',
        children: [
          {
            key: 'orders-sent',
            path: '/orders/sent',
            display: 'tab',
          },
          {
            key: 'orders-buyer',
            path: '/orders/buyer',
            children: [
              {
                key: 'orders-buyer-details',
                display: 'none',
                path: '/orders/buyer/details/[id]',
                backButton: '/orders/buyer',
              },
              {
                key: 'orders-sent-details',
                path: '/orders/sent/details/[id]',
                display: 'none',
                backButton: '/orders/sent',
              },
            ],
            display: 'tab',
          },
        ],
      },
      {
        key: 'order-seller-quote-details',
        path: '/orders/sentquote/details/[id]',
        display: 'none',
        backButton: '/orders/sentquote',
      },
      {
        key: 'order-request-details',
        path: '/orders/request/details/[id]',
        display: 'none',
        backButton: '/orders/request',
      },
      {
        key: 'orders-sent-details',
        path: '/orders/sent/details/[id]',
        display: 'none',
        backButton: '/orders/sent',
      },
      {
        key: 'order-seller-details',
        path: '/orders/seller/details/[id]',
        display: 'none',
        backButton: '/orders/seller',
      },
      {
        key: 'orders-buyer-details',
        path: '/orders/buyer/details/[id]',
        display: 'none',
        backButton: '/orders/buyer',
      },
      {
        key: 'orders-message',
        path: '/orders/sentquote/message/[id]',
        display: 'none',
        backButton: '/orders/sentquote/details/[id]',
      },
      {
        key: 'orders-message',
        path: '/orders/request/message/[id]',
        display: 'none',
        backButton: '/orders/request/details/[id]',
      },
      {
        key: 'orders-message',
        path: '/orders/sent/message/[id]',
        display: 'none',
        backButton: '/orders/sent/details/[id]',
      },
      {
        key: 'orders-message',
        path: '/orders/seller/message/[id]',
        display: 'none',
        backButton: '/orders/seller/details/[id]',
      },
      {
        key: 'orders-message',
        path: '/orders/buyer/message/[id]',
        display: 'none',
        backButton: '/orders/buyer/details/[id]',
      },
      {
        key: 'create-quote',
        navBarTitleKey: 'create-quote-details',
        path: '/orders/quote',
        display: 'none',
      },
      {
        key: 'orders-customers-directory-details',
        navBarTitleKey: 'orders-customers-directory-details',
        path: '/orders/customers',
        display: 'navbar',
      },
      {
        key: 'orders-directory',
        navBarTitleKey: 'orders-directory-details',
        path: '/orders/documents',
        display: 'navbar',
        children: [
          {
            key: 'orders-directory-documents',
            path: '/orders/documents',
            display: 'tab',
          },
          {
            key: 'orders-directory-services',
            path: '/orders/services',
            display: 'tab',
          },
          {
            key: 'orders-directory-settings',
            path: '/orders/parameters',
            display: 'tab',
          },
        ],
      },
    ],
  },
  {
    key: 'organization',
    navBarTitleKey: 'organization-details',
    path: '/organization',
    icon: <DynamicIcon name="IdentificationCard" className="size-5 shrink-0" />,
    display: 'navbar',
    group: 'admin',
    authorizedRoles: ['ADMIN'],
    children: [
      {
        key: 'organization-general',
        path: '/organization',
        display: 'tab',
      },
      {
        key: 'organization-directory',
        path: '/organization/directory',
        display: 'tab',
      },
      {
        key: 'organization-directory-details',
        path: '/organization/directory/[id]',
        display: 'none',
      },
    ],
  },
  {
    key: 'team',
    navBarTitleKey: 'team-details',
    path: '/team',
    icon: <DynamicIcon name="Users" className="size-5 shrink-0" />,
    display: 'navbar',
    group: 'admin',
    authorizedRoles: ['ADMIN'],
    children: [
      {
        key: 'team-general',
        navBarTitleKey: 'team-general-details',
        path: '/team/settings',
        display: 'none',
        backButton: '/team',
      },
    ],
  },
  {
    key: 'settings',
    path: '/settings',
    display: 'navbar',
    group: 'admin',
    authorizedRoles: ['ADMIN', 'USER'],
    icon: <DynamicIcon name="GearSix" className="size-5 shrink-0" />,
    children: [
      {
        key: 'qr-code',
        navBarTitleKey: 'qr-code-details',
        path: '/settings/tracking-tags',
        display: 'navbar',
      },
      {
        key: 'settings-export',
        navBarTitleKey: 'settings-export-details',
        path: '/settings/export',
        display: 'tab',
      },
      {
        key: 'settings-subscription',
        path: '/settings/subscription',
        display: 'tab',
      },
    ],
  },
  {
    key: 'profil',
    navBarTitleKey: 'account-settings',
    path: '/account',
    display: 'none',
  },
]

export const findNavItem = (
  pathname: string,
  id?: string,
): NavItem | undefined => {
  const findItem = (
    items: NavItem[],
    parent?: NavItem,
  ): NavItem | undefined => {
    for (const item of items) {
      // 1. Exact match check
      if (item.path === pathname) {
        return { ...item, parent, id }
      }

      // 2. Pattern match for dynamic routes (e.g. [id])
      // Only attempt if the path definition actually contains dynamic segments like [id]
      if (item.path.includes('[')) {
        // Create regex from path: replace [something] with ([^/]+)
        // We escape special chars except brackets which we're replacing
        const pattern = '^' + item.path.replace(/\[[^\]]+\]/g, '([^/]+)') + '$'
        const regex = new RegExp(pattern)

        if (regex.test(pathname)) {
          return { ...item, parent, id }
        }
      }

      if (item.children) {
        const childItem = findItem(item.children, item)
        if (childItem) {
          return childItem
        }
      }
    }
    return undefined
  }

  return findItem(navItems)
}
